import { proceduralTerrain } from '@/modules/game-procedural/procedural';
import type { WorldTerrainContext } from '@/modules/game-procedural/procedural';

export const WORLD_UNITS_PER_PX = 25;
export const TERRAIN_REBAKE_THRESHOLD = WORLD_UNITS_PER_PX * 0.45;

export const TERRAIN_BG: Record<string, string> = {
  grass: '#3a7a28',
  road: '#8e7550',
  dirt: '#7c6242',
  stone: '#6c7078',
  plaza: '#6c6c75',
  forest_floor: '#2d5a24',
  ruins_floor: '#5a5a58',
  canyon_floor: '#8b7355',
  swamp_floor: '#3d5c42',
  volcanic_floor: '#5c3830',
  astral_floor: '#4a4580',
};

export const DEFAULT_BG = '#1e2420';

function hexToRgb(hex: string): [number, number, number] {
  const s = hex.replace('#', '');
  return [
    parseInt(s.slice(0, 2), 16),
    parseInt(s.slice(2, 4), 16),
    parseInt(s.slice(4, 6), 16),
  ];
}

export const TERRAIN_RGB = new Map<string, [number, number, number]>(
  Object.entries(TERRAIN_BG).map(([k, v]) => [k, hexToRgb(v)]),
);
export const DEFAULT_RGB = hexToRgb(DEFAULT_BG);

export type TerrainSliceCache = {
  w: number;
  h: number;
  centerX: number;
  centerY: number;
  worldKey: string;
  canvas: HTMLCanvasElement;
};

export function bakeTerrainSlice(
  w: number,
  h: number,
  centerX: number,
  centerY: number,
  miniCtx: WorldTerrainContext | null,
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  const cx = w / 2;
  const cy = h / 2;
  const scale = WORLD_UNITS_PER_PX;

  if (!miniCtx || miniCtx.towns.length === 0) {
    const terrain = proceduralTerrain(Math.floor(centerX), Math.floor(centerY), null);
    ctx.fillStyle = TERRAIN_BG[terrain] ?? DEFAULT_BG;
    ctx.fillRect(0, 0, w, h);
    return canvas;
  }

  const img = ctx.createImageData(w, h);
  const data = img.data;
  let q = 0;
  for (let py = 0; py < h; py++) {
    const wy = Math.floor(centerY + (py - cy) * scale);
    for (let px = 0; px < w; px++) {
      const wx = Math.floor(centerX + (px - cx) * scale);
      const terrain = proceduralTerrain(wx, wy, miniCtx);
      const rgb = TERRAIN_RGB.get(terrain) ?? DEFAULT_RGB;
      data[q++] = rgb[0];
      data[q++] = rgb[1];
      data[q++] = rgb[2];
      data[q++] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

export function terrainCacheNeedsRebake(
  cache: TerrainSliceCache | null,
  w: number,
  h: number,
  heroX: number,
  heroY: number,
  worldKey: string,
): boolean {
  if (!cache) return true;
  if (cache.w !== w || cache.h !== h) return true;
  if (cache.worldKey !== worldKey) return true;
  if (
    Math.abs(heroX - cache.centerX) >= TERRAIN_REBAKE_THRESHOLD ||
    Math.abs(heroY - cache.centerY) >= TERRAIN_REBAKE_THRESHOLD
  ) {
    return true;
  }
  return false;
}

export function drawTerrainFromCache(
  ctx: CanvasRenderingContext2D,
  cache: TerrainSliceCache,
  heroX: number,
  heroY: number,
  dw: number,
  dh: number,
): void {
  const u = WORLD_UNITS_PER_PX;
  const { centerX: tcx, centerY: tcy, canvas: off } = cache;
  const ow = off.width;
  const oh = off.height;

  ctx.fillStyle = DEFAULT_BG;
  ctx.fillRect(0, 0, dw, dh);

  const sx = (heroX - tcx) / u;
  const sy = (heroY - tcy) / u;
  const srcX0 = Math.max(0, Math.floor(sx));
  const srcY0 = Math.max(0, Math.floor(sy));
  const srcX1 = Math.min(ow, Math.ceil(sx + dw));
  const srcY1 = Math.min(oh, Math.ceil(sy + dh));
  if (srcX0 < srcX1 && srcY0 < srcY1) {
    const destX = srcX0 - sx;
    const destY = srcY0 - sy;
    const iw = srcX1 - srcX0;
    const ih = srcY1 - srcY0;
    ctx.drawImage(off, srcX0, srcY0, iw, ih, destX, destY, iw, ih);
  }
}
